/**
 * Removes the given search params from a relative URL, leaving the path, the
 * remaining params and the hash untouched.
 *
 * @param href - A relative URL, e.g. `/asset-screener?search=ACRED#top`.
 * @param keys - The search param keys to remove.
 * @returns The URL without those params.
 */
export function stripSearchParams(href: string, keys: string[]): string {
    const hashIndex = href.indexOf("#");
    const hash = hashIndex === -1 ? "" : href.slice(hashIndex);
    const beforeHash = hashIndex === -1 ? href : href.slice(0, hashIndex);

    const queryIndex = beforeHash.indexOf("?");
    if (queryIndex === -1) {
        return href;
    }

    const path = beforeHash.slice(0, queryIndex);
    const params = new URLSearchParams(beforeHash.slice(queryIndex + 1));
    keys.forEach((key) => params.delete(key));

    const search = params.toString();
    return `${path}${search ? `?${search}` : ""}${hash}`;
}

/**
 * Removes the given search params from the address bar without triggering a
 * navigation. This is the replacement for
 * `router.replace(url, undefined, { shallow: true })`, which `next/navigation`
 * does not have.
 *
 * NOTE - the Pages Router keeps its routing state (`__N`) on the history entry
 * and reads it back on back/forward. Its popstate handler ignores an entry
 * without that state, so the rendered page would stop matching the URL. The
 * state is therefore carried over, with the params stripped from it as well.
 * The App Router patches `replaceState` itself and only needs the new URL.
 *
 * @param keys - The search param keys to remove.
 */
export function removeSearchParamsFromUrl(keys: string[]): void {
    const { pathname, search, hash } = window.location;
    const url = stripSearchParams(`${pathname}${search}${hash}`, keys);

    const state = window.history.state;
    if (state?.__N) {
        window.history.replaceState(
            {
                ...state,
                url: stripSearchParams(state.url, keys),
                as: stripSearchParams(state.as, keys),
            },
            "",
            url,
        );
        return;
    }

    window.history.replaceState(null, "", url);
}
